// Lightweight table primitive for resource list pages. Generic over
// the row type so columns can render strongly-typed values.
//
// Each `Column<T>.render(row)` returns whatever React node the cell
// should display; default renderer reads `row[col.key]`.

import type { ReactNode } from "react";

import { EmptyState } from "./EmptyState";

export interface Column<T> {
  key: keyof T | string;
  header: ReactNode;
  render?: (row: T) => ReactNode;
  width?: string | number;
  align?: "left" | "right" | "center";
}

interface TableProps<T> {
  columns: ReadonlyArray<Column<T>>;
  rows: ReadonlyArray<T>;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  caption?: ReactNode;
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty,
  caption,
}: TableProps<T>) {
  if (rows.length === 0) {
    return empty !== undefined ? (
      <>{empty}</>
    ) : (
      <EmptyState title="No rows" />
    );
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.9rem",
        }}
      >
        {caption && (
          <caption
            style={{
              padding: "8px 0",
              textAlign: "left",
              color: "var(--text-dim)",
              fontSize: "0.85rem",
            }}
          >
            {caption}
          </caption>
        )}
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={String(col.key)}
                style={{
                  textAlign: col.align ?? "left",
                  padding: "10px 12px",
                  borderBottom: "1px solid var(--border)",
                  color: "var(--text-dim)",
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  width: col.width,
                  whiteSpace: "nowrap",
                }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
              style={{
                cursor: onRowClick ? "pointer" : undefined,
                borderBottom: "1px solid var(--border)",
              }}
            >
              {columns.map((col) => {
                const value = col.render
                  ? col.render(row)
                  : ((row as Record<string, unknown>)[String(col.key)] as ReactNode);
                return (
                  <td
                    key={String(col.key)}
                    style={{
                      padding: "10px 12px",
                      textAlign: col.align ?? "left",
                      verticalAlign: "middle",
                    }}
                  >
                    {value}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
